"use client";

/**
 * Route-level error boundary.
 *
 * The site previously had no boundary at all, so any throw during hydration
 * (a motion island fighting React for the DOM, a browser API behaving
 * differently under an unusual client) unmounted the whole tree and left a
 * blank white page with nothing to diagnose from.
 *
 * Individual motion islands are now contained by MotionSafe. This is the
 * backstop beneath them: the header, footer and booking CTA from the root
 * layout stay mounted, so the visitor still has navigation and a way to
 * book while the failed route segment shows a recovery action.
 */
import { useEffect } from "react";
import Link from "next/link";
import { FRESHA_BOOKING_URL } from "@/lib/constants";

export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[route error]", error);
  }, [error]);

  return (
    <section className="mx-auto flex max-w-[42rem] flex-col items-start gap-6 px-5 py-24 sm:px-8 sm:py-32">
      <p className="silk-eyebrow">
        <span className="silk-eyebrow-rule" />
        <span>Something went wrong</span>
      </p>
      <h1 className="silk-display-2 text-ink">
        This page did not load properly.
      </h1>
      <p className="silk-body text-ink/75">
        The fault is at our end, not yours. Reloading usually resolves it. If
        it does not, our treatments and pricing are all still reachable below,
        and online booking is unaffected.
      </p>
      <div className="flex flex-wrap items-center gap-4">
        <button type="button" onClick={reset} className="silk-pill">
          <span>Try again</span>
        </button>
        <Link href="/treatments" className="silk-pill silk-pill--secondary">
          <span>Browse treatments</span>
        </Link>
        <a
          href={FRESHA_BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="silk-pill silk-pill--secondary"
        >
          <span>Book online</span>
        </a>
      </div>
      {error.digest ? (
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink/40">
          Reference {error.digest}
        </p>
      ) : null}
    </section>
  );
}
